import React, { useEffect, useState, useMemo } from "react";
import {
  Box, Card, Typography, Tabs, Tab, Grid, TextField, Button, Table,
  TableHead, TableRow, TableCell, TableBody, Paper, Alert,
  CircularProgress, Chip
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend
} from "recharts";
import {
  getSalesReport, getStockReport, getCustomerReport
} from "../services/api";

const fmtMoney = (v) =>
  Number(v || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " ₺";
const fmtMoneyShort = (v) => {
  const n = Number(v || 0);
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M ₺";
  if (n >= 1_000) return (n / 1_000).toFixed(1) + "B ₺";
  return n.toLocaleString("tr-TR", { maximumFractionDigits: 0 }) + " ₺";
};
const fmtDate = (s) => s ? new Date(s).toLocaleDateString("tr-TR") : "—";
const fmtDateTime = (s) => s ? new Date(s).toLocaleString("tr-TR") : "—";

const isoDate = (d) => d.toISOString().slice(0, 10);
const todayIso = () => isoDate(new Date());
const daysAgoIso = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return isoDate(d); };

function exportToExcel(rows, columns, sheetName, fileName) {
  if (!rows || rows.length === 0) {
    toast.error("Dışa aktarılacak veri yok");
    return;
  }
  const data = rows.map(r => {
    const out = {};
    columns.forEach(c => { out[c.label] = c.format ? c.format(r[c.key], r) : (r[c.key] ?? ""); });
    return out;
  });
  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  XLSX.writeFile(wb, `${fileName}_${todayIso()}.xlsx`);
  toast.success("Excel dosyası indirildi");
}

export default function Reports() {
  const [tab, setTab] = useState(0);
  const [from, setFrom] = useState(daysAgoIso(30));
  const [to, setTo] = useState(todayIso());
  const [loading, setLoading] = useState(false);
  const [sales, setSales] = useState([]);
  const [stock, setStock] = useState([]);
  const [customers, setCustomers] = useState([]);

  const loadData = () => {
    setLoading(true);
    if (tab === 0) {
      getSalesReport(from, to)
        .then(r => setSales(Array.isArray(r.data) ? r.data : []))
        .catch(e => { toast.error("Satış raporu yüklenemedi"); setSales([]); })
        .finally(() => setLoading(false));
    } else if (tab === 1) {
      getStockReport()
        .then(r => setStock(Array.isArray(r.data) ? r.data : []))
        .catch(() => { toast.error("Stok raporu yüklenemedi"); setStock([]); })
        .finally(() => setLoading(false));
    } else {
      getCustomerReport(from, to)
        .then(r => setCustomers(Array.isArray(r.data) ? r.data : []))
        .catch(() => { toast.error("Müşteri raporu yüklenemedi"); setCustomers([]); })
        .finally(() => setLoading(false));
    }
  };

  useEffect(() => { loadData(); /* eslint-disable-next-line */ }, [tab]);

  const salesTotals = useMemo(() => {
    const totalOrders = sales.reduce((s, r) => s + Number(r.order_count || 0), 0);
    const totalRev = sales.reduce((s, r) => s + Number(r.revenue || 0), 0);
    const avgBasket = totalOrders > 0 ? totalRev / totalOrders : 0;
    return { totalOrders, totalRev, avgBasket };
  }, [sales]);

  const stockSummary = useMemo(() => {
    const total = stock.length;
    const low = stock.filter(p => Number(p.stock) <= Number(p.min_stock || 10)).length;
    const zero = stock.filter(p => Number(p.stock) === 0).length;
    return { total, low, zero };
  }, [stock]);

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h5" fontWeight={800}>📈 Raporlar</Typography>
      </Box>

      {/* Tarih filtresi */}
      <Card sx={{ p: 2.5, borderRadius: 3, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4} md={3}>
            <TextField
              type="date" label="Başlangıç" size="small" fullWidth
              value={from} onChange={e => setFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={tab === 1}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <TextField
              type="date" label="Bitiş" size="small" fullWidth
              value={to} onChange={e => setTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={tab === 1}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={6} sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button variant="contained" onClick={loadData} startIcon={<RefreshIcon />}>
              Uygula
            </Button>
            <Button size="small" onClick={() => { setFrom(daysAgoIso(7)); setTo(todayIso()); }}>Son 7g</Button>
            <Button size="small" onClick={() => { setFrom(daysAgoIso(30)); setTo(todayIso()); }}>Son 30g</Button>
            <Button size="small" onClick={() => { setFrom(daysAgoIso(90)); setTo(todayIso()); }}>Son 90g</Button>
          </Grid>
        </Grid>
      </Card>

      <Card sx={{ borderRadius: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tab label="Satış" />
          <Tab label="Stok" />
          <Tab label="Müşteri" />
        </Tabs>

        <Box p={2.5}>
          {loading ? (
            <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
          ) : tab === 0 ? (
            <SalesTab rows={sales} totals={salesTotals} />
          ) : tab === 1 ? (
            <StockTab rows={stock} summary={stockSummary} />
          ) : (
            <CustomerTab rows={customers} />
          )}
        </Box>
      </Card>
    </Box>
  );
}

function SalesTab({ rows, totals }) {
  const chart = rows.map(r => ({
    date: new Date(r.date).toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit" }),
    Sipariş: Number(r.order_count) || 0,
    Ciro: Number(r.revenue) || 0
  }));

  const handleExport = () => exportToExcel(rows, [
    { key: "date", label: "Tarih", format: (v) => fmtDate(v) },
    { key: "order_count", label: "Sipariş Sayısı", format: (v) => Number(v || 0) },
    { key: "revenue", label: "Ciro (₺)", format: (v) => Number(v || 0) },
    { key: "avg_basket", label: "Ort. Sepet (₺)", format: (v) => Number(v || 0) },
  ], "Satış", "satis_raporu");

  return (
    <>
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} sm={4}>
          <Card sx={{ p: 2, borderRadius: 2, bgcolor: "#1565C011" }}>
            <Typography variant="caption" color="text.secondary">Toplam Sipariş</Typography>
            <Typography variant="h5" fontWeight={800}>{totals.totalOrders.toLocaleString("tr-TR")}</Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ p: 2, borderRadius: 2, bgcolor: "#10B98111" }}>
            <Typography variant="caption" color="text.secondary">Toplam Ciro</Typography>
            <Typography variant="h5" fontWeight={800}>{fmtMoneyShort(totals.totalRev)}</Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ p: 2, borderRadius: 2, bgcolor: "#F59E0B11" }}>
            <Typography variant="caption" color="text.secondary">Ortalama Sepet</Typography>
            <Typography variant="h5" fontWeight={800}>{fmtMoneyShort(totals.avgBasket)}</Typography>
          </Card>
        </Grid>
      </Grid>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Typography variant="subtitle2" fontWeight={700}>Günlük Satışlar</Typography>
        <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={handleExport}>
          Excel İndir
        </Button>
      </Box>

      {rows.length === 0 ? (
        <Alert severity="info">Bu tarih aralığında satış verisi yok.</Alert>
      ) : (
        <>
          <Box mb={2.5}>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={chart} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="left" tick={{ fontSize: 11 }} allowDecimals={false} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }}
                       tickFormatter={(v) => fmtMoneyShort(v)} />
                <Tooltip formatter={(val, name) => name === "Ciro" ? fmtMoney(val) : val} />
                <Legend />
                <Line yAxisId="left" type="monotone" dataKey="Sipariş" stroke="#1565C0" strokeWidth={2} />
                <Line yAxisId="right" type="monotone" dataKey="Ciro" stroke="#10B981" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </Box>
          <Paper elevation={0} sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ "& th": { fontWeight: 700, color: "text.secondary", fontSize: 12 } }}>
                  <TableCell>Tarih</TableCell>
                  <TableCell align="right">Sipariş</TableCell>
                  <TableCell align="right">Ciro</TableCell>
                  <TableCell align="right">Ort. Sepet</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(r => (
                  <TableRow key={r.date} hover>
                    <TableCell>{fmtDate(r.date)}</TableCell>
                    <TableCell align="right">{Number(r.order_count).toLocaleString("tr-TR")}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: "#1565C0" }}>
                      {fmtMoney(r.revenue)}
                    </TableCell>
                    <TableCell align="right">{fmtMoney(r.avg_basket)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}
    </>
  );
}

function StockTab({ rows, summary }) {
  const chartData = useMemo(() => {
    const byCat = {};
    rows.forEach(r => {
      const c = r.category || "Diğer";
      byCat[c] = (byCat[c] || 0) + Number(r.stock || 0);
    });
    return Object.entries(byCat)
      .map(([category, stock]) => ({ category: category.length > 18 ? category.slice(0, 16) + "…" : category, stock }))
      .sort((a, b) => b.stock - a.stock).slice(0, 10);
  }, [rows]);

  const handleExport = () => exportToExcel(rows, [
    { key: "name", label: "Ürün" },
    { key: "category", label: "Kategori" },
    { key: "sku", label: "SKU" },
    { key: "stock", label: "Mevcut Stok", format: (v) => Number(v || 0) },
    { key: "min_stock", label: "Min Stok", format: (v) => Number(v || 0) },
    { key: "unit", label: "Birim" },
    { key: "price", label: "Fiyat (₺)", format: (v) => Number(v || 0) },
    { key: "updated_at", label: "Son Hareket", format: (v) => fmtDateTime(v) },
  ], "Stok", "stok_raporu");

  return (
    <>
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} sm={4}>
          <Card sx={{ p: 2, borderRadius: 2, bgcolor: "#1565C011" }}>
            <Typography variant="caption" color="text.secondary">Toplam Ürün</Typography>
            <Typography variant="h5" fontWeight={800}>{summary.total.toLocaleString("tr-TR")}</Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ p: 2, borderRadius: 2, bgcolor: "#F59E0B11" }}>
            <Typography variant="caption" color="text.secondary">Düşük Stok</Typography>
            <Typography variant="h5" fontWeight={800} sx={{ color: "#F59E0B" }}>
              {summary.low.toLocaleString("tr-TR")}
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ p: 2, borderRadius: 2, bgcolor: "#EF444411" }}>
            <Typography variant="caption" color="text.secondary">Stok Sıfır</Typography>
            <Typography variant="h5" fontWeight={800} sx={{ color: "#EF4444" }}>
              {summary.zero.toLocaleString("tr-TR")}
            </Typography>
          </Card>
        </Grid>
      </Grid>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Typography variant="subtitle2" fontWeight={700}>Ürün Stokları</Typography>
        <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={handleExport}>
          Excel İndir
        </Button>
      </Box>

      {rows.length === 0 ? (
        <Alert severity="info">Stok verisi yok.</Alert>
      ) : (
        <>
          <Box mb={2.5}>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="category" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="stock" fill="#1565C0" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
          <Paper elevation={0} sx={{ overflowX: "auto", maxHeight: 480 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ "& th": { fontWeight: 700, color: "text.secondary", fontSize: 12 } }}>
                  <TableCell>Ürün</TableCell>
                  <TableCell>Kategori</TableCell>
                  <TableCell align="right">Stok</TableCell>
                  <TableCell align="right">Min</TableCell>
                  <TableCell align="right">Fiyat</TableCell>
                  <TableCell>Son Hareket</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(p => {
                  const stockNum = Number(p.stock);
                  const minNum = Number(p.min_stock || 10);
                  const tone = stockNum === 0 ? "#EF4444" : stockNum <= minNum ? "#F59E0B" : "#10B981";
                  return (
                    <TableRow key={p.id} hover>
                      <TableCell sx={{ maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {p.name}
                      </TableCell>
                      <TableCell>{p.category || "—"}</TableCell>
                      <TableCell align="right">
                        <Chip label={`${stockNum} ${p.unit || ""}`} size="small"
                          sx={{ bgcolor: tone + "22", color: tone, fontWeight: 700, fontSize: 11 }} />
                      </TableCell>
                      <TableCell align="right" sx={{ color: "text.secondary", fontSize: 12 }}>{minNum}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{fmtMoney(p.price)}</TableCell>
                      <TableCell sx={{ color: "text.secondary", fontSize: 12 }}>{fmtDateTime(p.updated_at)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}
    </>
  );
}

function CustomerTab({ rows }) {
  const top10 = rows.slice(0, 10).map(r => ({
    name: (r.company || r.name || "").slice(0, 18),
    Ciro: Number(r.total_revenue) || 0
  }));

  const handleExport = () => exportToExcel(rows, [
    { key: "name", label: "Ad" },
    { key: "company", label: "Firma" },
    { key: "email", label: "E-posta" },
    { key: "phone", label: "Telefon" },
    { key: "order_count", label: "Sipariş Sayısı", format: (v) => Number(v || 0) },
    { key: "total_revenue", label: "Toplam Ciro (₺)", format: (v) => Number(v || 0) },
    { key: "last_order_at", label: "Son Sipariş", format: (v) => fmtDateTime(v) },
  ], "Müşteri", "musteri_raporu");

  const totalRev = rows.reduce((s, r) => s + Number(r.total_revenue || 0), 0);
  const totalOrd = rows.reduce((s, r) => s + Number(r.order_count || 0), 0);

  return (
    <>
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} sm={4}>
          <Card sx={{ p: 2, borderRadius: 2, bgcolor: "#1565C011" }}>
            <Typography variant="caption" color="text.secondary">Aktif Müşteri</Typography>
            <Typography variant="h5" fontWeight={800}>{rows.length.toLocaleString("tr-TR")}</Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ p: 2, borderRadius: 2, bgcolor: "#10B98111" }}>
            <Typography variant="caption" color="text.secondary">Toplam Sipariş</Typography>
            <Typography variant="h5" fontWeight={800}>{totalOrd.toLocaleString("tr-TR")}</Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ p: 2, borderRadius: 2, bgcolor: "#8B5CF611" }}>
            <Typography variant="caption" color="text.secondary">Toplam Ciro</Typography>
            <Typography variant="h5" fontWeight={800}>{fmtMoneyShort(totalRev)}</Typography>
          </Card>
        </Grid>
      </Grid>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Typography variant="subtitle2" fontWeight={700}>Müşteri Performansı</Typography>
        <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={handleExport}>
          Excel İndir
        </Button>
      </Box>

      {rows.length === 0 ? (
        <Alert severity="info">Bu tarih aralığında müşteri siparişi yok.</Alert>
      ) : (
        <>
          <Box mb={2.5}>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={top10}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-20} textAnchor="end" height={60} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => fmtMoneyShort(v)} />
                <Tooltip formatter={(v) => fmtMoney(v)} />
                <Bar dataKey="Ciro" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
          <Paper elevation={0} sx={{ overflowX: "auto", maxHeight: 480 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ "& th": { fontWeight: 700, color: "text.secondary", fontSize: 12 } }}>
                  <TableCell>Ad</TableCell>
                  <TableCell>Firma</TableCell>
                  <TableCell align="right">Sipariş</TableCell>
                  <TableCell align="right">Toplam Ciro</TableCell>
                  <TableCell>Son Sipariş</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(r => (
                  <TableRow key={r.id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{r.name || "—"}</TableCell>
                    <TableCell sx={{ color: "text.secondary" }}>{r.company || "—"}</TableCell>
                    <TableCell align="right">{Number(r.order_count).toLocaleString("tr-TR")}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: "#1565C0" }}>
                      {fmtMoney(r.total_revenue)}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>
                      {fmtDateTime(r.last_order_at)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}
    </>
  );
}
